import type {SlideRendererCallback} from 'react-swipeable-views-utils';

import {Block, Fade} from '@travel-frontend/ui-components';
import {useCallback, useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {mod} from 'react-swipeable-views-core';

import {Container} from '@/components/ImageCarousel/components/Container';
import {Image} from '@/components/ImageCarousel/components/Image';
import {VirtualizedSwipeableViews} from '@/components/ImageCarousel/components/VirtualizedSwipeableViews';
import {ImageGalleryNextControl} from '@/components/ImageGalleryNextControl';
import {ImageGalleryPreviousControl} from '@/components/ImageGalleryPreviousControl';

const springConfig = {
  duration: '0.45s',
  easeFunction: 'cubic-bezier(0.15, 0.3, 0.25, 1)',
  delay: '0.05s',
};

type Props = {
  images: Array<string>;
  isRounded?: boolean;
  hasMouseEvents?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  onClickPrev?: React.MouseEventHandler<HTMLDivElement>;
  onClickNext?: React.MouseEventHandler<HTMLDivElement>;
  // internal
  slideIndex: number;
  onChangeSlideIndex: (index: number) => void;
  imageLoadingStrategy?: 'eager' | 'lazy';
};

export const ImageCarousel: React.FunctionComponent<Props> = ({
  images,
  isRounded = false,
  hasMouseEvents = true,
  children,
  onClick,
  onClickPrev,
  onClickNext,
  // internal
  slideIndex,
  onChangeSlideIndex,
  imageLoadingStrategy = 'eager',
  ...restProps
}) => {
  const [hovering, setHovering] = useState(false);

  const [overscan, setOverscan] = useState(0);
  const [visibilityRef, inView] = useInView({
    rootMargin: '0px 0px 400px 0px',
    fallbackInView: true,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      setOverscan(1);
    }
  }, [inView]);

  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);

  const slideRenderer: SlideRendererCallback = useCallback(
    ({index, key}) => {
      return (
        <Image
          loading={imageLoadingStrategy}
          alt=""
          key={key}
          src={images[mod(index, images.length)]}
          data-testid="image-carousel-image"
        />
      );
    },
    [images, imageLoadingStrategy]
  );

  let content = (
    <Image
      alt=""
      src={images[0]}
      data-testid="image-carousel-image"
      loading={imageLoadingStrategy}
    />
  );

  if (images.length !== 1 && overscan) {
    content = (
      <Fade
        in
        mountOnEnter
        duration={imageLoadingStrategy === 'eager' ? 0 : undefined}
      >
        <Block position="relative" height="100%">
          <VirtualizedSwipeableViews
            index={slideIndex}
            onChangeIndex={onChangeSlideIndex}
            overscanSlideBefore={overscan}
            overscanSlideAfter={overscan}
            slideRenderer={slideRenderer}
            springConfig={springConfig}
            enableMouseEvents={hasMouseEvents}
            tabIndex={onClick ? -1 : undefined}
            data-testid="image-carousel-container"
          />
          <ImageGalleryPreviousControl
            highlighted={hovering}
            onClick={onClickPrev}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          />
          <ImageGalleryNextControl
            highlighted={hovering}
            onClick={onClickNext}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          />
        </Block>
      </Fade>
    );
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      // Only trigger opening gallery on list views where onClick is provided
      if (onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Container
      {...restProps}
      isRounded={isRounded}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchEnd={handleMouseLeave}
      ref={visibilityRef}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
    >
      {content}
      {children}
    </Container>
  );
};
